import React, { useState } from 'react';
import { TrendingUp, Circle, Trophy, Dumbbell, Star, Clapperboard, Wifi, Search, Smartphone, Bot, FlaskConical, Landmark, Lightbulb, Brain, GraduationCap, Music, Film, Gamepad2, Palette, Shirt, Heart, Plane, Globe, Rocket, Mountain, PawPrint, Waves, UtensilsCrossed, Stethoscope, Flame, Car, Shield } from 'lucide-react';
import { INTEREST_OPTIONS, CATEGORY_GROUPS, getCategoriesByGroup } from '../data/categories';

const ICON_MAP: Record<string, React.FC<{ size?: number; className?: string }>> = {
  TrendingUp, Circle, Trophy, Dumbbell, Star, Clapperboard, Wifi, Search, Smartphone, Bot, FlaskConical, Landmark, Lightbulb, Brain, GraduationCap, Music, Film, Gamepad2, Palette, Shirt, Heart, Plane, Globe, Rocket, Mountain, PawPrint, Waves, UtensilsCrossed, Stethoscope, Flame, Car, Shield
};

interface OnboardingProps {
  onComplete: (interests: string[]) => void;
}

export const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const grouped = getCategoriesByGroup();

  const toggleInterest = (id: string) => {
    setSelectedInterests(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleContinue = () => {
    const interests = selectedInterests.length > 0 ? selectedInterests : INTEREST_OPTIONS.map(o => o.id);
    onComplete(interests);
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black flex flex-col overflow-hidden">
      <div className="px-6 pt-14 pb-6">
        <h1 className="text-[28px] font-bold text-white leading-tight">
          What interests you?
        </h1>
        <p className="text-white/35 text-[15px] mt-1.5 font-normal">
          Choose topics for your feed{selectedInterests.length > 0 && ` · ${selectedInterests.length} selected`}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-32">
        <div className="max-w-lg mx-auto space-y-7">
          {CATEGORY_GROUPS.map(group => (
            <div key={group}>
              <p className="text-[13px] font-medium text-white/25 mb-3">{group}</p>
              <div className="flex flex-wrap gap-2">
                {grouped[group].map(option => {
                  const isSelected = selectedInterests.includes(option.id);
                  const Icon = ICON_MAP[option.icon];
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleInterest(option.id)}
                      className={`
                        inline-flex items-center gap-2 pl-3.5 pr-4 py-2.5 rounded-full transition-all duration-150 active:scale-[0.96]
                        ${isSelected
                          ? 'bg-white text-black'
                          : 'bg-white/[0.06] text-white/50'
                        }
                      `}
                    >
                      {Icon && <Icon size={15} strokeWidth={isSelected ? 2.2 : 1.8} />}
                      <span className="text-[13px] font-medium whitespace-nowrap">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/95 to-transparent pt-12">
        <button
          onClick={handleContinue}
          className="w-full py-[14px] bg-white text-black rounded-full font-semibold text-[15px] active:scale-[0.98] transition-transform"
        >
          {selectedInterests.length > 0
            ? `Continue`
            : 'Skip'
          }
        </button>
      </div>
    </div>
  );
};
